import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  calculateWpm,
  calculateAccuracy,
  formatDuration,
  getCharStatus,
  isLessonComplete,
} from './typingTutorUtils';
import { getComboMultiplier } from './typingTutorGames';

/**
 * Core typing engine — character-by-character feedback (Typing.com / Monkeytype style)
 */
export default function TypingEngine({ lesson, targetWpm, onComplete, onRetry, onCancel }) {
  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const [combo, setCombo] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const target = lesson?.text || '';

  const reset = useCallback(() => {
    setTyped('');
    setStarted(false);
    setElapsed(0);
    setFinished(false);
    setResult(null);
    setCombo(0);
    startTimeRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onRetry?.();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [onRetry]);

  useEffect(() => {
    reset();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [lesson?.id, reset]);

  useEffect(() => {
    const handleKey = (e) => {
      if (finished) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        setCombo(0);
        setTyped((prev) => prev.slice(0, -1));
        return;
      }
      if (e.key.length !== 1) return;
      e.preventDefault();

      if (!started) {
        setStarted(true);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 250);
      }

      setTyped((prev) => {
        if (prev.length >= target.length) return prev;
        const next = prev + e.key;
        const isCorrect = e.key === target[prev.length];
        if (isCorrect) setCombo((c) => c + 1);
        else setCombo(0);
        if (isLessonComplete(next, target)) {
          setTimeout(() => finishLesson(next), 0);
        }
        return next;
      });
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [finished, started, target, onCancel]);

  const finishLesson = (finalTyped) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const timeSeconds = Math.max(
      1,
      startTimeRef.current
        ? Math.floor((Date.now() - startTimeRef.current) / 1000)
        : elapsed || 1,
    );
    let correct = 0;
    let errors = 0;
    for (let i = 0; i < finalTyped.length; i += 1) {
      if (finalTyped[i] === target[i]) correct += 1;
      else errors += 1;
    }
    const wpm = calculateWpm(correct, timeSeconds);
    const accuracy = calculateAccuracy(correct, finalTyped.length);
    const sessionResult = {
      wpm,
      accuracy,
      timeSpentSeconds: timeSeconds,
      errors,
      totalChars: finalTyped.length,
      correctChars: correct,
      goalMet: wpm >= (targetWpm || 35),
    };
    setResult(sessionResult);
    setFinished(true);
    onComplete?.(sessionResult);
  };

  const liveWpm = started
    ? calculateWpm(
        typed.split('').filter((ch, i) => ch === target[i]).length,
        Math.max(1, elapsed),
      )
    : 0;

  const liveAccuracy = typed.length > 0
    ? calculateAccuracy(
        typed.split('').filter((ch, i) => ch === target[i]).length,
        typed.length,
      )
    : 100;

  const comboMultiplier = getComboMultiplier(combo);

  const renderChars = () =>
    target.split('').map((char, index) => {
      const status = getCharStatus(typed, target, index);
      const isCurrent = index === typed.length && !finished;
      let className = 'tt-char-pending';
      if (status === 'correct') className = 'tt-char-correct';
      if (status === 'incorrect') className = 'tt-char-incorrect';
      if (isCurrent) className += ' tt-char-current';
      return (
        <span key={index} className={className}>
          {char}
        </span>
      );
    });

  if (finished && result) {
    return (
      <Box className="tt-result-panel tt-card">
        <Typography variant="h5" className="tt-sans" fontWeight={700} gutterBottom>
          Lesson Complete
        </Typography>
        <Box className="tt-stats-bar" sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.wpm}</div>
            <div className="tt-stat-label">WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.accuracy}%</div>
            <div className="tt-stat-label">Accuracy</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{formatDuration(result.timeSpentSeconds)}</div>
            <div className="tt-stat-label">Time</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.errors}</div>
            <div className="tt-stat-label">Errors</div>
          </div>
        </Box>
        <Typography
          className="tt-sans"
          sx={{ color: result.goalMet ? 'success.main' : 'text.secondary', mb: 2 }}
        >
          {result.goalMet
            ? `Goal reached! You met the ${targetWpm} WPM target.`
            : `Keep practicing — goal is ${targetWpm} WPM.`}
        </Typography>
        <Button variant="outlined" startIcon={<ReplayIcon />} onClick={reset} sx={{ mr: 1 }}>
          Try Again
        </Button>
        <Button variant="contained" color="success" onClick={onCancel}>
          Back to Lessons
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="tt-stats-bar" sx={{ mb: 2 }}>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{liveWpm}</div>
          <div className="tt-stat-label">WPM</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{liveAccuracy}%</div>
          <div className="tt-stat-label">Accuracy</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{formatDuration(elapsed)}</div>
          <div className="tt-stat-label">Time</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">
            {Math.round((typed.length / Math.max(target.length, 1)) * 100)}%
          </div>
          <div className="tt-stat-label">Progress</div>
        </div>
        {combo >= 5 && (
          <div className="tt-stat-item tt-combo-stat">
            <div className="tt-stat-value">×{comboMultiplier.toFixed(1)}</div>
            <div className="tt-stat-label">{combo} streak</div>
          </div>
        )}
      </Box>

      <div
        className="tt-typing-area tt-mono"
        onClick={() => inputRef.current?.focus()}
        role="textbox"
        tabIndex={0}
        aria-label="Typing practice area"
      >
        {renderChars()}
      </div>

      <input
        ref={inputRef}
        className="tt-hidden-input"
        autoFocus
        aria-hidden="true"
        tabIndex={-1}
      />

      <Typography className="tt-keyboard-hint tt-sans">
        {!started
          ? 'Start typing to begin the timer. Focus on accuracy first.'
          : 'Press Esc to go back. Backspace to correct mistakes.'}
      </Typography>
    </Box>
  );
}
